/**
 * Utility functions for building price options in property search filters
 */

/**
 * Builds an array of price options based on provided parameters
 * @param {Object} config - Configuration object
 * @param {number} config.max - Maximum price value
 * @param {number} [config.min=0] - Minimum price value
 * @param {number} [config.step=50000] - Step increment for prices
 * @param {number[]} [config.hardStops=[]] - Specific price values to include
 * @param {string} [config.noLabel='No min'] - Label for the empty option
 * @returns {Array<{value: string, label: string}>} Array of price options
 */
export const buildPriceOptions = ({
  max,
  min = 0,
  step = 50000,
  hardStops = [],
  noLabel = 'No min',
}) => {
  const options = [{ value: '', label: noLabel }];
  let current = Math.ceil(Number(max) / step) * step;
  const seen = new Set();

  while (current >= min && current >= step) {
    if (!seen.has(current)) {
      options.push({
        value: String(current),
        label: `£${current.toLocaleString()}`,
      });
      seen.add(current);
    }
    current -= step;
  }

  hardStops.forEach((stop) => {
    if (!seen.has(stop) && stop >= min && stop <= max) {
      options.push({
        value: String(stop),
        label: `£${stop.toLocaleString()}`,
      });
      seen.add(stop);
    }
  });

  options.splice(
    1,
    options.length - 1,
    ...options
      .slice(1)
      .sort((a, b) => Number(a.value || 0) - Number(b.value || 0))
  );
  return options;
};

/**
 * Builds minimum price options with predefined configuration
 * @param {number} maxPriceData - Maximum price data from the backend
 * @returns {Array<{value: string, label: string}>} Array of minimum price options
 */
export const buildMinPriceOptions = (maxPriceData) => {
  return buildPriceOptions({
    max: maxPriceData,
    min: 50000,
    step: 50000,
    hardStops: [1000000],
    noLabel: 'No min',
  });
};

/**
 * Builds maximum price options with predefined configuration
 * @param {number} maxPriceData - Maximum price data from the backend
 * @returns {Array<{value: string, label: string}>} Array of maximum price options
 */
export const buildMaxPriceOptions = (maxPriceData) => {
  return buildPriceOptions({
    max: maxPriceData,
    min: 100000,
    step: 50000,
    hardStops: [1000000, 1500000, 2000000],
    noLabel: 'No max',
  });
};
